import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";

interface Props {
  Menuprops: menuInfo;
}

const SN_COS_MAS_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRef = useRef<SnGridRef>(null);
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseColsRef = useRef<any[]>([]); // 정적 컬럼 원본(메타)
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any[]>([]);
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} }); // 그리드 상단 버튼(sys_headerctrl)
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const { userInfo } = global();

  useEffect(() => {
    const fetchform = async () => {
      try {
        // 원가기간 초기값: 최신 마감월(공통 메타 쿼리)
        const _rtnYm: any = await fnGetDataSet({
          id: "com_CommonMapper_12",
          params: { cd_firm: userInfo.cd_firm, cd_biz: userInfo.cd_biz },
        });
        const _dt = Array.isArray(_rtnYm?.[0]) ? _rtnYm[0] : [];
        const newData = _dt?.[0]
          ? { dt_from: _dt[0].ym_start, dt_to: _dt[0].ym }
          : {};

        const rtn: any = await getInitFormData();
        baseColsRef.current = rtn.grid.grid1 ?? [];
        setFormData({ ...rtn.formData, ...newData });
        setFormControlData(rtn.formControlData);
        setColums(baseColsRef.current);
        setHeaderInfo(rtn.header?.grid1 ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region 원가요소 동적 컬럼(피벗)
  // data[1](원가요소 목록)으로 동적 컬럼을 만들어 정적 컬럼 뒤·합계금액 앞에 붙인다.
  const buildGridColumns = (elemRows: any[]) => {
    const baseCols = baseColsRef.current ?? [];
    // 합계금액(am_prd) 컬럼은 항상 맨 뒤로 보내고, 원가요소 컬럼 템플릿으로도 쓴다.
    const totalCol = baseCols.find((c: any) => c.cd_column === "am_prd");
    const staticCols = baseCols.filter((c: any) => c.cd_column !== "am_prd");
    const template =
      totalCol ??
      baseCols.find((c: any) => c.tp_column === "SnNumberBox") ??
      baseCols[0] ??
      {};

    const maxSeq = staticCols.reduce(
      (m: number, c: any) => Math.max(m, Number(c.no_seq ?? 0)),
      0
    );

    const elemCols = (elemRows ?? []).map((e: any, i: number) => ({
      ...template,
      cd_column: e.cd_elem,
      nm_column: e.nm_elem || e.cd_elem,
      cd_clas: null,
      cd_popup: null,
      dc_editcon: null,
      fg_visible: "Y",
      yn_sum: "Y",
      no_seq: maxSeq + 1 + i,
    }));

    if (!totalCol) return [...staticCols, ...elemCols];
    return [
      ...staticCols,
      ...elemCols,
      { ...totalCol, no_seq: maxSeq + 1 + elemCols.length },
    ];
  };

  // data[0](품목 base)에 data[2](품목×요소 금액)를 원가요소 컬럼값으로 펼친다.
  const buildGridRows = (itemRows: any[], valueRows: any[]) => {
    const rows = (itemRows ?? []).map((r: any) => ({ ...r }));
    const rowByItem = new Map<string, any>();
    rows.forEach((r: any) => rowByItem.set(String(r.cd_item), r));

    (valueRows ?? []).forEach((v: any) => {
      const row = rowByItem.get(String(v.cd_item));
      if (!row) return;
      row[v.cd_elem] = Number(row[v.cd_elem] ?? 0) + Number(v.am_prd ?? 0);
    });
    return rows;
  };
  //#endregion

  const onSearch = async () => {
    try {
      const f = searchRef.current.getFormdata();
      const params = {
        id: "sn_cos_mas_reg_007_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: f.cd_biz,
          ym_end: f.dt_to.toString().substring(0, 6),
        },
      };
      const data: any = await fnGetDataSet(params);
      const itemRows = data?.[0] ?? []; // 품목 base
      const elemRows = data?.[1] ?? []; // 원가요소 목록(동적 컬럼)
      const valueRows = data?.[2] ?? []; // 품목×원가요소 금액

      setColums(buildGridColumns(elemRows));
      setDataSource1(buildGridRows(itemRows, valueRows));
    } catch (error) {}
  };

  //#region 원가집계
  const onAggregate = async () => {
    const f = searchRef.current.getFormdata();
    const cd_biz = f.cd_biz;
    const ym_start = f.dt_from.toString().substring(0, 6);
    const ym_end = f.dt_to.toString().substring(0, 6);

    const cntRtn: any = await fnGetDataSet({
      id: "sn_cos_mas_reg_007_05",
      params: { cd_firm: userInfo.cd_firm, cd_biz, ym_end },
    });
    const cnt = cntRtn?.[0] ?? [];
    if (cnt?.[0] && Number(cnt[0].cnt) !== 0) {
      const ok = await showDialog(
        "confirm",
        "이미 처리된 내역이 있습니다. 삭제하고 다시 생성하겠습니까?"
      );
      if (!ok) return;
    }

    const res = await callApi("SN_COS_MAS_REG_007_Btn", {
      cd_firm: userInfo.cd_firm,
      cd_biz,
      ym_start,
      ym_end,
    });
    if (res?.status) {
      await onSearch();
    }
  };
  //#endregion

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 min-h-0 mt-1">
        <SnGrid
          ref={gridRef}
          columnConfig={columns}
          rowData={dataSource1}
          etcProps={{
            rowSelection: "multiple",
            isEditable: false,
            title: "제품별배부",
          }}
          headerInfo={headerInfo}
          events={{
            onBtnEtcClick: (sender: string) => {
              if (sender === "btnAgg") onAggregate();
            },
          }}
        />
      </div>
    </div>
  );
});

export default React.memo(SN_COS_MAS_REG_007);
